import { Icon } from '@synergy/icons';
import { Button, Col, Flex, Row, Switch, Tooltip } from "antd";
import clsx from "clsx";
import i18next from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { WidgetCard } from "../../../styles/global/Widget.styled";
import Theme from "../../../styles/Theme/Theme";
import { Map } from "../../Pages/Map";
import { SimpleMapComponent } from "../Organism/Maps/SimpleMapComponent";
import { RenderChart } from "./RenderChart";
import { SelectAtomic } from "./SelectAtomic";
import { Text } from "./Text";

const WidgetContent = styled.div`
  padding: ${(props) => props.$padding ?? (Theme.spacing['s05'] && Theme.spacing['s03'])};
  height:100%;
  pointer-events: auto;
  .hidden{
      display: none;
  }
`;

const SwitchTitle = styled.span`
  color: ${props => (props.disabled ? "gray" : "inherit")};
  text-decoration: ${props => (props.disabled ? "line-through" : "none")};
  margin-left: 10px;
`;
const DisabledText = styled.span`
  color: red; 
  margin-left: 10px; 
`;

export const Widget = ({
  data,
  fetchIsLoading,
  onButtonClick,
  props,
  ...rest
}) => {
  const { t } = useTranslation();
  
  let map;
  if (data?.map?.type == 'mapEvents') {
    map = <SimpleMapComponent mapId={data?.map} height={data?.height} />;
  } else {
    map = <Map mapId={data?.map} />;
  }

  return (
    <WidgetCard
      $minHeight={data?.height}
      $width={data?.width}
      $backgroundColor={data?.background}
      $border={data?.border}
      $justifyContent={data?.justify}
      $flexDirection={data?.direction}
      className={clsx(rest?.className)}
      $activeshadow={data?.activeShadow}
      $position={data?.position}
    >
      {/* Contem o titulo, possiveis botões à direita e possivel icon de informação à direita */}
      <div className="widget-header" style={{ marginRight: data?.buttons && (data?.tooltip || data?.dropdowns) ? "2%" : null }}>
        {/* Mostra titulo do widget à esquerda */}
        <div className="widget-title">
          <Icon icon={data?.icon} iconsize="sm" color={data?.iconColor ?? "teal-4"} bgColor={!data?.icon && data?.iconColor ? data?.iconColor : null} />
          {data?.title ? (
            <Text
              $fontColor={data?.titlColor ? data?.titlColor : "coolgray-12"}
              $marginLeft="s03"
              $fontSize="text_md"
              $weight="medium"
            >
              {t(data?.title)}
            </Text>
          ) : null}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: data.gap ?? "1rem" }}>
          {/* Mostra botões ao canto direito */}
          {data?.buttons ? (
            <Flex gap="small" >
              {data.buttons.map((button, index) => (
                <Button
                  className={
                    (button.isActive ? "ant-btn ant-btn-blue" : "ant-btn-outline") +
                    " -xsmall"
                  }
                  key={button?.key ?? index}
                  title={button?.title}
                  onClick={(value) => {
                    button.onButtonClick(value);
                  }
                  }
                >
                  {" "}
                  {button?.title}{" "}
                </Button>
              ))}
            </Flex>
          ) : null}

          {data?.switch ? (
            <Flex gap="small" >
              {data.switch?.map((sw, index) => (
                <div style={{ width: sw.width, display: sw.display }} key={index}>
                  <Switch onChange={() => sw.onClick()} disabled={sw.isToDisable} />
                  <SwitchTitle disabled={sw.isToDisable}>{sw.title}</SwitchTitle>
                  {(sw.isToDisable && sw.isToDisableText) && <DisabledText>{sw.isToDisableText}</DisabledText>}
                </div>
              ))}
            </Flex>
          ) : null}

          {/* Mostra dropdowns especificos ao widget no canto direito */}
          {data?.dropdowns ? (
            <Flex gap="small">
              {data.dropdowns.map((dropdown, index) => (
                <div style={{ width: dropdown?.width }} key={index}>
                  <SelectAtomic
                    optionsData={dropdown?.data}
                    handleOptionsChange={(value, record) => {
                      // setDatePeriod(value);
                      dropdown.functionChange(value);
                    }}
                    defaultValue={dropdown?.value}
                    fullWidth={true}
                    isToDisable={dropdown?.isToDisable}
                  />
                </div>
              ))}
            </Flex>
          ) : null}

          {/* Mostra icon para tooltip ao canto direito */}
          {data?.tooltip ? (
            <Tooltip
              className="widget-tooltip"
              title={data?.tooltip} //Recebe a informação para renderizar na tooltip
              color={Theme.color.white}
              colorText={Theme.color["coolgray-12"]}
            >
              <Button
                shape="circle"
                type="text"
                icon={
                  <Icon icon={"Information"} iconsize="md" color="blue-4" />
                }
              />
            </Tooltip>
          ) : null}
        </div>
      </div>

      {/* Renderiza o gráfico */}
      <WidgetContent $padding={data.padding}>
        {data?.charts ? (
          <Row gutter={16}>
            {data?.charts.map((el, index) => (
              <Col
                key={index}
                span={el.cols ?? 24}
                className={
                  data?.charts.length == 1 ? "full-height chart" : "chart"
                }
              >
                <RenderChart
                  key={index}
                  chartdata={el?.data}
                  charttype={el?.chartType}
                  chartquery={el?.chartQuery}
                  idQuery={el.idQuery}
                  width={el?.width ?? "100%"}
                  height={el?.height ?? null}
                  chartId={el?.chartId}
                  fetchIsLoading={fetchIsLoading}
                  language={i18next.resolvedLanguage}
                  title={el?.title}
                  isFetching={el?.isFetching}
                  params={el?.params}
                  props={props}
                />
              </Col>
            ))}
          </Row>
        ) : null}

        {/* Renderiza um mapa dentro do widget */}
        {data?.map ? (
          <Row gutter={16} className="full-height">
            <Col span={24}>{map}</Col>
          </Row>
        ) : null}

        {/*Renderiza um componente children dentro */}
        {rest.children}
      </WidgetContent>
    </WidgetCard>
  );
};
